import React from 'react';
import { emLangLabel, emSummary } from '../../email/utils';
import type { EmAttachment, EmThread } from '../../email/types';
import { Icon } from '../Icon/Icon';
import { EmailAttachmentPreviewList } from './EmailAttachmentPreview';
import { EmailMessageBody } from './EmailMessageBody';
import { EmailSeenBy } from './EmailSeenBy';

type ReplyMode = 'reply' | 'replyAll' | 'forward';

export interface EmailMessageRowProps {
  thread: EmThread;
  message: EmThread['messages'][number];
  open: boolean;
  showTranslated?: boolean;
  onToggle: () => void;
  onReply: (mode: ReplyMode) => void;
  onOpenAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  onDownloadAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  loadAttachmentPreview?: ((attachment: EmAttachment, messageId: string) => Promise<string | null>) | undefined;
}

function initialsOf(name: string) {
  return name
    .split(' ')
    .filter((w) => w.length > 1)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function EmailMessageRow({
  thread,
  message: m,
  open,
  showTranslated = false,
  onToggle,
  onReply,
  onOpenAttachment,
  onDownloadAttachment,
  loadAttachmentPreview,
}: EmailMessageRowProps) {
  const isAI = m.who === 'elva';
  const showTr = Boolean(showTranslated && m.bodyTr);
  const attachmentNames = m.attachments?.map((attachment) => attachment.name);
  const bodyProps = attachmentNames?.length ? { attachmentNames } : {};
  const showSeenBy = m.who === 'patient' && Boolean(m.seenBy?.length || m.seenCount);

  return (
    <div
      className={`em-msg ${isAI ? 'ai' : ''} ${open ? 'expanded' : 'collapsed'}`}
      onClick={open ? undefined : onToggle}
      role={open ? undefined : 'button'}
      tabIndex={open ? undefined : 0}
    >
      <div className="em-msg-head" onClick={open ? onToggle : undefined} style={{ cursor: 'pointer' }}>
        <span
          className={`ms-av ${isAI ? 'ms-av-elva' : 'ms-av-patient'}`}
          style={{
            width: 34,
            height: 34,
            fontSize: 11.5,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 600,
            fontFamily: 'var(--font-display)',
            flex: 'none',
          }}
        >
          {isAI ? <Icon kind="sparkle" size={15} /> : initialsOf(m.from)}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{isAI ? 'Elva' : m.from}</span>
            {isAI ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--ai-3)',
                }}
              >
                <Icon kind="sparkle" size={9} />
                AI
              </span>
            ) : null}
            {!open ? (
              <span
                style={{
                  marginLeft: 'auto',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--ink-4)',
                  flex: 'none',
                }}
              >
                {m.time}
              </span>
            ) : null}
          </div>
          {open ? (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isAI ? `to ${m.to}` : `${m.fromEmail} · to you`}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 3 }}>
              <Icon kind="sparkle" size={10} stroke={2} style={{ color: 'var(--ai-3)', flex: 'none', marginTop: 2 }} />
              <span
                style={{
                  fontSize: 12.5,
                  color: 'var(--ink-2)',
                  lineHeight: 1.45,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {emSummary(thread.id, m)}
              </span>
            </div>
          )}
          {!open && showSeenBy ? (
            <EmailSeenBy receipts={m.seenBy ?? []} {...(m.seenCount != null ? { seenCount: m.seenCount } : {})} compact />
          ) : null}
        </div>
        {open ? (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--ink-4)',
              flex: 'none',
              alignSelf: 'flex-start',
              marginTop: 2,
            }}
          >
            {m.time}
          </span>
        ) : null}
        <Icon
          kind={open ? 'up' : 'down'}
          size={14}
          style={{
            color: 'var(--ink-4)',
            flex: 'none',
            alignSelf: open ? 'flex-start' : 'center',
            marginTop: open ? 3 : 0,
          }}
        />
      </div>

      {open ? (
        <>
          <div className="em-msg-body">
            {showTr ? (
              <>
                <EmailMessageBody text={m.bodyTr ?? ''} {...bodyProps} />
                <div
                  style={{
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: '1px dashed var(--cream-2)',
                    fontStyle: 'italic',
                    color: 'var(--ink-3)',
                    fontSize: 12.5,
                  }}
                >
                  <Icon kind="translate" size={11} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                  <span>
                    Original{m.lang ? ` (${emLangLabel(m.lang)})` : ''}:
                  </span>
                  <div style={{ marginTop: 6 }}>
                    <EmailMessageBody text={m.body} {...bodyProps} />
                  </div>
                </div>
              </>
            ) : (
              <EmailMessageBody text={m.body} {...bodyProps} />
            )}
            {m.bodyOriginal ? (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px dashed color-mix(in srgb, var(--ai-3) 30%, transparent)',
                  fontStyle: 'italic',
                  color: 'var(--ink-3)',
                  fontSize: 12.5,
                }}
              >
                <Icon kind="translate" size={11} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                <span>Sent as:</span>
                <div style={{ marginTop: 6 }}>
                  <EmailMessageBody text={m.bodyOriginal} {...bodyProps} />
                </div>
              </div>
            ) : null}
            {m.attachments?.length ? (
              <EmailAttachmentPreviewList
                attachments={m.attachments}
                messageId={m.id}
                enabled={open}
                onOpenAttachment={onOpenAttachment}
                onDownloadAttachment={onDownloadAttachment}
                loadAttachmentPreview={loadAttachmentPreview}
              />
            ) : null}
            {showSeenBy ? (
              <EmailSeenBy receipts={m.seenBy ?? []} {...(m.seenCount != null ? { seenCount: m.seenCount } : {})} />
            ) : null}
          </div>
          <div className="em-msg-actions">
            <button type="button" className="em-act" onClick={() => onReply('reply')}>
              <Icon kind="undo" size={13} />
              Reply
            </button>
            <button type="button" className="em-act" onClick={() => onReply('replyAll')}>
              <Icon kind="team" size={13} />
              Reply all
            </button>
            <button type="button" className="em-act" onClick={() => onReply('forward')}>
              <Icon kind="arrow-r" size={13} />
              Forward
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
